import {
    annotationLayer,
    store as annotationLayerStore,
    type AnnotationLayerEnabledActions,
    type AnnotationLayerSettings,
    type ChartsDataPoint,
    type CommentMarkerNumbers,
    CagrArrowAction,
    canDrawCagrArrow,
} from "@zebrabi/annotation-layer";
import { ChartType, type ChartData, type DataPoint } from "@zebrabi/charts";
import { VisualType } from "@zebrabi/constants";
import type { MessageBarService } from "@zebrabi/ui-components";

import { ANNOTATIONS_READ_MORE_URL } from "../consts";

/** The floating menu's action list, as the annotation layer's initialize and setEnabledActions take it. */
export type EnabledAnnotationActions = AnnotationLayerEnabledActions["enabledActions"];

function toChartsDataPoint(dataPoint: DataPoint, chartData: ChartData): ChartsDataPoint {
    return {
        ...dataPoint,
        group: chartData.group,
        dataPointSelection: dataPoint.dataPointSelection,
        allChildDataPoints: [],
        hasPBIComment: false,
        isCollapsed: false,
        canPlotComment: false,
        pbiFormat: undefined,
    };
}

/** The part of the charts view model the Add CAGR arrow rule reads. */
export interface CagrArrowViewModel {
    isSingleSeriesViewModel: boolean;
    isMultiples: boolean;
}

/** The part of the chart settings the Add CAGR arrow rule reads. */
export interface CagrArrowSettings {
    shouldPlotVerticalCharts(): boolean;
    getRenderChartType(isMultiples: boolean): ChartType;
}

/**
 * Whether the floating menu offers Add CAGR arrow for the chart about to render. Mirrors checkIfCagrButtonShouldBeAdded
 * in the Power BI charts visual: one measure, horizontal layout, no waterfall.
 */
export function canAddCagrArrow(viewModel: CagrArrowViewModel | undefined, settings: CagrArrowSettings | undefined): boolean {
    if (!viewModel || !settings) {
        return false;
    }

    return (
        viewModel.isSingleSeriesViewModel
        && !settings.shouldPlotVerticalCharts()
        && settings.getRenderChartType(viewModel.isMultiples) !== ChartType.Waterfall
    );
}

/**
 * Whether the arrow Add CAGR arrow creates, first data point to last, can be drawn on the chart about to render.
 * Picks the ends the way the charts CAGR renderer does: blank points are skipped first, and with small multiples every
 * chart draws its own arrow, so one drawable chart is enough.
 */
function canDrawDefaultCagrArrow(chartData: ChartData[]): boolean {
    return chartData.some(({ dataPoints }: ChartData) => {
        const plottedPoints = dataPoints.filter((dataPoint: DataPoint) => dataPoint.value !== null);
        if (plottedPoints.length < 2) {
            return false;
        }

        return canDrawCagrArrow(plottedPoints[0].value, plottedPoints[plottedPoints.length - 1].value);
    });
}

/** Office has no comments, so no marker ever gets a number. */
function numberNoCommentMarkers(): CommentMarkerNumbers {
    return { numberOf: new Map(), anchors: new Set(), next: 1 };
}

/**
 * Feeds the annotation-layer store the data points and settings the charts CAGR renderer reads, and initializes the
 * layer (with its floating menu) on the first call. Must run before each charts update, the way the Power BI charts
 * visual's annotationLayerInit does.
 */
export function prepareAnnotationLayer(
    hostElement: HTMLElement,
    clickActionParentSelector: string,
    chartData: ChartData[],
    enabledActions: EnabledAnnotationActions,
    messageBarService: MessageBarService,
    getAnnotationLayerSettings: () => AnnotationLayerSettings
): void {
    for (const { group, dataPoints } of chartData) {
        for (const dataPoint of dataPoints) {
            dataPoint.dataPointSelection = { identifier: { fullGroup: group ?? "", fullCategory: dataPoint.category } };
        }
    }

    annotationLayerStore.setAnnotationLayerSettings(getAnnotationLayerSettings());
    annotationLayerStore.setTransformDataPointFunction((dataPoint: DataPoint) =>
        toChartsDataPoint(
            dataPoint,
            chartData.find((cd: ChartData) => cd.dataPoints.includes(dataPoint))
        )
    );
    annotationLayerStore.setDataPoints(
        chartData.flatMap((cd: ChartData) => cd.dataPoints.map((dp: DataPoint) => toChartsDataPoint(dp, cd)))
    );

    if (!annotationLayer.isInitialized) {
        annotationLayerStore.setVisualType(VisualType.CHARTS);
        annotationLayer.initialize(
            hostElement,
            clickActionParentSelector,
            // No CreateComment: comments are not supported in Office.
            {
                enabledActions,
                enabledCagrActions: [CagrArrowAction.Settings, CagrArrowAction.Delete],
                readMoreUrl: ANNOTATIONS_READ_MORE_URL,
            },
            messageBarService,
            numberNoCommentMarkers
        );
    }

    annotationLayer.setEnabledActions(enabledActions);
    annotationLayer.refreshAddCagrArrowButtonState(canDrawDefaultCagrArrow(chartData));
}
